// In-content embeds — widgets rendered from links found in the post TEXT (delta_json), not from
// answer.url or answer.embeds[]: stream embeds, leaderboard/TDP cards, and the single
// text-derived (twitter-only in read mode) LinkPreview. This module extracts them with the SAME
// pipeline Answer.jsx's read-mode render uses (extractUrls + suppressed-preview filtering +
// per-family config gates) so the static-geometry height estimator can reserve exactly what the
// flag-gated render pins.
//
// Memoized by answer-object identity (module WeakMap — same pattern/justification as
// answerHeightContentSignature in answerHeightFingerprint.ts): the entity cache replaces answer
// objects on every real write and never mutates in place, and the estimator runs per row per
// render on the scroll path, so one extraction per object suffices.
import {
    extractUrls,
    extractStreamEmbedConfig,
    twitterRegexp,
} from 'components/shared/linkPreviewUrls'
import { filterSuppressedPreviewUrls } from 'components/answers/payloadRemoval'
import { getDeltaSummary } from 'components/answers/largeDelta'

export type ContentEmbedLinks = {
    // Post-suppression families, mirroring Answer.jsx's read-mode render inputs. Entries are
    // preview keys/hrefs — the estimator only needs identity + counts.
    stream: string[]
    leaderboard: string[]
    tdp: string[]
    market: string[]
    // Read mode only surfaces twitter links from text as link previews
    // (getTwitterLinkPreviews in Answer.jsx).
    twitter: string[]
    // delta_json.stream_invite / delta_json.host_invite empty linkPreviews in read
    // mode (both render a CTA card instead).
    streamInvite: boolean
    // delta_json.daily_earnings renders the notification share-button chrome.
    dailyEarnings: boolean
    // delta_json.host_revenue renders the stream-earnings card below the body.
    hostRevenue: boolean
}

const EMPTY_CONTENT_EMBED_LINKS: ContentEmbedLinks = Object.freeze({
    stream: [],
    leaderboard: [],
    tdp: [],
    market: [],
    twitter: [],
    streamInvite: false,
    dailyEarnings: false,
    hostRevenue: false,
}) as ContentEmbedLinks

const stringArray = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter(entry => typeof entry === 'string' && entry) : []

const previewKey = (entry: unknown): string | null => {
    if (typeof entry === 'string' && entry) return entry
    const key = (entry as { previewUrl?: unknown } | null)?.previewUrl
    return typeof key === 'string' && key ? key : null
}

const previewKeys = (entries: unknown[]): string[] =>
    entries.map(previewKey).filter((key): key is string => !!key)

export function hasContentEmbedLinks(links: ContentEmbedLinks): boolean {
    return !!(
        links.stream.length ||
        links.leaderboard.length ||
        links.tdp.length ||
        links.market.length ||
        links.twitter.length ||
        links.streamInvite ||
        links.dailyEarnings ||
        links.hostRevenue
    )
}

function computeContentEmbedLinks(answer: any): ContentEmbedLinks {
    // Fingerprint-shaped source (see answerHeightFingerprint): the retained extraction output —
    // already suppression-filtered at extraction time — substitutes for the evicted delta_json.
    const retained = answer.content_embed_links
    if (retained && typeof retained === 'object') {
        return {
            stream: stringArray(retained.stream),
            leaderboard: stringArray(retained.leaderboard),
            tdp: stringArray(retained.tdp),
            market: answer.market_card === true ? stringArray(retained.market) : [],
            twitter: stringArray(retained.twitter),
            streamInvite: !!retained.stream_invite,
            dailyEarnings: !!retained.daily_earnings,
            hostRevenue: !!retained.host_revenue,
        }
    }

    const deltaJson = answer.delta_json
    if (!deltaJson) return EMPTY_CONTENT_EMBED_LINKS
    // Read mode skips URL extraction entirely for oversized deltas (Answer.jsx's
    // answerDeltaSummary.oversized guard).
    if (getDeltaSummary(deltaJson).oversized) {
        return {
            ...EMPTY_CONTENT_EMBED_LINKS,
            streamInvite: !!((deltaJson as any)?.stream_invite || (deltaJson as any)?.host_invite),
            dailyEarnings: !!(deltaJson as any)?.daily_earnings,
            hostRevenue: !!(deltaJson as any)?.host_revenue,
        }
    }

    const extracted = extractUrls(deltaJson) || {}
    // Read-mode suppression: answerModel.mergeHiddenPreviewUrls merges ephemeral edit-session
    // state (empty for inactive rows) with the draft's persisted suppressed_preview_urls, which
    // the draft whitelist copies from the answer — so the answer's own list is the pure input.
    const suppressed = Array.isArray(answer.suppressed_preview_urls)
        ? answer.suppressed_preview_urls
        : []
    const stream = filterSuppressedPreviewUrls(extracted.streamEmbedLinks || [], suppressed)
        // Mirrors the render's per-link gate: `if (!extractStreamEmbedConfig(href)) return null`.
        .filter((href: string) => !!extractStreamEmbedConfig(href))
    const leaderboard = filterSuppressedPreviewUrls(
        extracted.leaderboardEmbedLinks || [],
        suppressed,
    )
    const tdp = filterSuppressedPreviewUrls(extracted.tdpEmbedLinks || [], suppressed)
    // A market delta is presentation-authoritative only on the server-vetted
    // market-card answer type. Client-authored posts can write arbitrary delta
    // attributes and must not reserve or render financial-card geometry.
    const market =
        answer.market_card === true
            ? filterSuppressedPreviewUrls(extracted.marketEmbedLinks || [], suppressed)
            : []
    const twitter = filterSuppressedPreviewUrls(
        // getTwitterLinkPreviews in Answer.jsx: only twitter links survive as read-mode previews.
        (extracted.nonKnovLinks || []).filter((url: string) => twitterRegexp.test(url)),
        suppressed,
    )

    if (!stream.length && !leaderboard.length && !tdp.length && !market.length && !twitter.length) {
        return {
            ...EMPTY_CONTENT_EMBED_LINKS,
            streamInvite: !!((deltaJson as any)?.stream_invite || (deltaJson as any)?.host_invite),
            dailyEarnings: !!(deltaJson as any)?.daily_earnings,
            hostRevenue: !!(deltaJson as any)?.host_revenue,
        }
    }

    return {
        stream: previewKeys(stream),
        leaderboard: previewKeys(leaderboard),
        tdp: previewKeys(tdp),
        market: previewKeys(market),
        twitter: previewKeys(twitter),
        streamInvite: !!((deltaJson as any)?.stream_invite || (deltaJson as any)?.host_invite),
        dailyEarnings: !!(deltaJson as any)?.daily_earnings,
        hostRevenue: !!(deltaJson as any)?.host_revenue,
    }
}

const contentEmbedLinksMemo = new WeakMap<object, ContentEmbedLinks>()

export function getContentEmbedLinks(answer: any): ContentEmbedLinks {
    if (!answer || typeof answer !== 'object') return EMPTY_CONTENT_EMBED_LINKS

    const memoized = contentEmbedLinksMemo.get(answer)
    if (memoized) return memoized

    let links = EMPTY_CONTENT_EMBED_LINKS
    try {
        links = computeContentEmbedLinks(answer)
    } catch {
        links = EMPTY_CONTENT_EMBED_LINKS
    }
    contentEmbedLinksMemo.set(answer, links)
    return links
}
